/* Tapestry Brew Guide: brewing data
 * Everything the guide knows about methods, equipment, recipes and fixes lives here.
 * The UI (tapestry-brew-guide.js) reads this file and never hard-codes brewing advice.
 *
 * To add a machine, brewer, symptom or fix, edit the lists below. No other file needs to change.
 * Coffee-specific recipes and notes come from each product's custom.brew_guide metafield.
 */
window.TapestryBrewData = (function () {
  'use strict';

  function r5(n) { return Math.round(n / 5) * 5; }
  function round(n) { return Math.round(n); }

  /* ---------- Roast buckets (from the 1–5 custom.roast_level metafield) ---------- */
  function roastBucket(roast) {
    var n = Number(roast);
    if (!n) return 'medium';
    if (n <= 2) return 'light';
    if (n >= 4) return 'dark';
    return 'medium';
  }
  var TEMP_F = { light: 205, medium: 200, dark: 195 };
  var ESPRESSO_TEMP_F = { light: 202, medium: 200, dark: 198 };
  var ESPRESSO_RATIO = { light: 2.3, medium: 2, dark: 1.8 };
  var ROAST_NOTE = {
    light: 'Lighter roast, so we\'ve set the water a little hotter to bring out its sweetness.',
    medium: '',
    dark: 'Darker roast, so we\'ve set the water a little cooler to keep it rich instead of harsh.'
  };
  var ESPRESSO_ROAST_NOTE = {
    light: 'Lighter roast, so we\'ve stretched the shot a little longer to balance its brightness.',
    medium: '',
    dark: 'Darker roast, so we\'ve kept the shot a little shorter to keep it rich and syrupy.'
  };

  function fToC(f) { return round((f - 32) * 5 / 9); }
  function oz(g) { return Math.round(g / 29.57 * 10) / 10; }
  function tbsp(g) { return Math.max(1, Math.round(g / 5 * 2) / 2); }
  function mmss(sec) { var m = Math.floor(sec / 60), s = sec % 60; return m + ':' + (s < 10 ? '0' : '') + s; }

  /* ---------- Methods ---------- */
  var METHODS = [
    { id: 'espresso', label: 'Espresso', sub: 'Home espresso machine', icon: 'espresso' },
    { id: 'pourover', label: 'Pour over', sub: 'V60, Chemex, Kalita', icon: 'pourover' },
    { id: 'drip', label: 'Drip machine', sub: 'Automatic coffee maker', icon: 'drip' },
    { id: 'french', label: 'French press', sub: 'Full-bodied, easy', icon: 'french' },
    { id: 'aeropress', label: 'AeroPress', sub: 'Quick single cup', icon: 'aeropress' },
    { id: 'cold', label: 'Cold brew', sub: 'Slow steep, smooth', icon: 'cold' }
  ];

  /* ---------- Equipment ---------- */
  // dose: starting dose in grams for that machine's standard double basket.
  // grinder: 'builtin' skips the grinder question.
  var MACHINES = [
    { id: 'breville-express', label: 'Breville Barista Express', dose: 18, grinder: 'builtin',
      finer: 'Turn the grind size dial one number lower.', coarser: 'Turn the grind size dial one number higher.',
      warmup: 'Turn the machine on about 10 minutes before brewing, with the portafilter locked in.' },
    { id: 'breville-pro', label: 'Breville Barista Pro', dose: 18, grinder: 'builtin',
      finer: 'Lower the grind setting by 2 numbers.', coarser: 'Raise the grind setting by 2 numbers.',
      warmup: 'It heats fast, but lock the portafilter in and run a few seconds of water through it before you brew.' },
    { id: 'breville-bambino', label: 'Breville Bambino', sub: 'Or Bambino Plus', dose: 18,
      warmup: 'It heats fast, but lock the portafilter in and run a few seconds of water through it before you brew.' },
    { id: 'delonghi-dedica', label: 'De\'Longhi Dedica', dose: 14,
      warmup: 'Turn it on 10–15 minutes early with the portafilter locked in.' },
    { id: 'delonghi-specialista', label: 'De\'Longhi La Specialista', dose: 18, grinder: 'builtin',
      finer: 'Turn the grind dial one notch finer.', coarser: 'Turn the grind dial one notch coarser.',
      warmup: 'Turn it on 10–15 minutes early with the portafilter locked in.' },
    { id: 'gaggia-classic', label: 'Gaggia Classic Pro', dose: 18,
      warmup: 'Give it a full 20 minutes to heat up with the portafilter locked in.' },
    { id: 'rancilio-silvia', label: 'Rancilio Silvia', dose: 18,
      warmup: 'Give it a full 20–30 minutes to heat up with the portafilter locked in. Right before brewing, run water until the heating light clicks on, then wait for it to click off.' },
    { id: 'other', label: 'Something else', sub: 'Or not sure', dose: 18,
      warmup: 'Give your machine at least 15 minutes to heat up with the portafilter locked in.' }
  ];

  var BREWERS = [
    { id: 'v60', label: 'Hario V60', sub: 'Or another cone dripper' },
    { id: 'chemex', label: 'Chemex' },
    { id: 'kalita', label: 'Kalita Wave', sub: 'Or another flat-bottom dripper' },
    { id: 'other', label: 'Something else', sub: 'Or not sure' }
  ];

  var GRINDERS = [
    { id: 'burr', label: 'Burr grinder', sub: 'Grinds between two discs or cones' },
    { id: 'blade', label: 'Blade grinder', sub: 'A spinning blade, like a spice grinder' },
    { id: 'preground', label: 'I buy it pre-ground' },
    { id: 'unknown', label: 'Not sure' }
  ];

  var BASKETS = [
    { id: 'standard', label: 'Lots of tiny holes', sub: 'A standard basket' },
    { id: 'pressurized', label: 'One small hole', sub: 'A pressurized or "dual wall" basket' },
    { id: 'unknown', label: 'Not sure' }
  ];

  var ROASTS = [
    { id: 'light', label: 'Light roast', sub: 'Often fruity, bright or floral', roast: 1.5 },
    { id: 'medium', label: 'Medium roast', sub: 'Balanced, chocolatey, caramel', roast: 3 },
    { id: 'dark', label: 'Dark roast', sub: 'Bold, roasty, low acidity', roast: 4.5 },
    { id: 'unknown', label: 'Not sure', sub: 'We\'ll start in the middle', roast: 3 }
  ];

  var GRIND = {
    espresso: ['Fine', 'a little finer than table salt'],
    pourover: ['Medium-fine', 'like table salt'],
    chemex: ['Medium-coarse', 'like coarse sand'],
    drip: ['Medium', 'like beach sand'],
    french: ['Coarse', 'like sea salt'],
    aeropress: ['Medium-fine', 'like table salt'],
    cold: ['Extra coarse', 'like cracked pepper']
  };

  /* ---------- Starting recipes ----------
   * Each builder gets { roast, brewer, machine, size, o } where o is the coffee's
   * metafield override for this method (may be empty) and returns a recipe card.
   */
  function tempStat(f) { return { label: 'Water temp', value: f + '°F', sub: fToC(f) + '°C' }; }
  function grindStat(key, o) {
    var g = GRIND[key];
    return { label: 'Grind', value: o.grind || g[0], sub: o.grind ? '' : g[1] };
  }

  var RECIPES = {
    espresso: {
      build: function (x) {
        var b = roastBucket(x.roast), o = x.o;
        var dose = o.dose || (x.machine && x.machine.dose) || 18;
        var ratio = o.ratio || (o.yield ? o.yield / (o.dose || dose) : ESPRESSO_RATIO[b]);
        var out = round(dose * ratio);
        var temp = o.temp_f || ESPRESSO_TEMP_F[b];
        var time = o.time || '25–32 seconds';
        var steps = [
          (x.machine && x.machine.warmup) || 'Let your machine fully heat up with the portafilter locked in.',
          'Grind ' + dose + ' g of coffee into the dry basket.',
          'Even out the grounds with a gentle tap or a quick stir, then tamp firm and level.',
          'Lock in, set your cup on a scale, and start the shot and a timer together.',
          'Stop the shot at ' + out + ' g in the cup. That should take ' + (/^(about|around|roughly)\b/i.test(time) ? time : 'about ' + time) + '.',
          'Taste it. Sour or thin? Grind a little finer next time. Bitter or dry? A little coarser.'
        ];
        var tips = [
          'A small scale under your cup is the easiest way to get consistent shots. Crema makes volume hard to judge.',
          'If your basket has a size stamped on it (like 18g), use that as your dose.'
        ];
        if (x.machine && x.machine.grinder === 'builtin') tips.unshift('Start your built-in grinder in the middle of its range and move one step at a time.');
        tips.push('Pre-ground coffee is too coarse for a standard espresso basket. If that\'s what you have, use the pressurized (dual wall) basket if your machine came with one.');
        return {
          stats: [
            { label: 'Coffee', value: dose + ' g', sub: 'ground' },
            { label: 'Espresso out', value: out + ' g', sub: 'in the cup' },
            { label: 'Ratio', value: '1:' + (Math.round(out / dose * 10) / 10) },
            { label: 'Shot time', value: time },
            { label: 'Temp', value: temp + '°F', sub: 'if your machine lets you set it' },
            grindStat('espresso', o)
          ],
          steps: steps, tips: tips,
          adjustNote: o.dose ? '' : ESPRESSO_ROAST_NOTE[b],
          numbers: { dose: dose, out: out, ratio: out / dose, temp: temp }
        };
      }
    },

    pourover: {
      // Cone and flat drippers top out around 30 g of coffee; a 6-cup Chemex handles about 50 g.
      size: { label: 'Cups', min: 1, max: function (s) { return s.brewer === 'chemex' ? 3 : 2; }, def: 1,
        unit: function (n) { return n === 1 ? '1 cup' : n + ' cups'; } },
      build: function (x) {
        var b = roastBucket(x.roast), o = x.o, br = x.brewer || 'v60';
        var ratio = o.ratio || 16;
        var n = x.size || 1;
        var dose = br === 'chemex' ? [25, 40, 50][Math.min(n, 3) - 1] : [20, 30][Math.min(n, 2) - 1];
        if (o.dose) dose = round(o.dose * n);
        var water = r5(dose * ratio);
        var temp = o.temp_f || TEMP_F[b];
        var bloom = r5(dose * 2.5);
        var steps, time, grindKey = br === 'chemex' ? 'chemex' : 'pourover';
        var first = 'Heat your water to ' + temp + '°F. Set a paper filter in the ' +
          (br === 'chemex' ? 'Chemex with the thick, triple-folded side over the spout,' : 'dripper,') +
          ' rinse it with hot water, and pour the rinse water out.';
        var second = 'Add ' + dose + ' g of ground coffee, give it a gentle shake to level the bed, and zero your scale.';
        var thirdBloom = 'Start a timer and pour ' + bloom + ' g of water, just enough to wet all the grounds. Give it a gentle swirl and wait until 0:45. This is the bloom: it lets gas escape so the water can do its job.';
        if (br === 'chemex') {
          time = o.time || '4:00–4:45';
          steps = [first, second, thirdBloom,
            'Pour slowly in small circles, avoiding the paper, until the scale reads ' + r5(water * 0.6) + ' g (around 1:30).',
            'Let it drop a little, then pour to ' + water + ' g by about 2:30.',
            'Let it drain. You\'re aiming to finish around ' + time + '. Lift out the filter and give the Chemex a swirl.'];
        } else if (br === 'kalita') {
          time = o.time || '3:15–3:45';
          steps = [first, second, thirdBloom,
            'Pour in slow circles to ' + r5(water * 0.45) + ' g by 1:00.',
            'Pour to ' + r5(water * 0.7) + ' g by 1:30.',
            'Pour to ' + water + ' g by 2:00, then let it drain. You\'re aiming to finish around ' + time + '.'];
        } else {
          time = o.time || (br === 'v60' ? '3:00–3:30' : '3:00–4:00');
          steps = [first, second, thirdBloom,
            'Pour slowly in small circles, avoiding the paper, until the scale reads ' + r5(water * 0.6) + ' g (around 1:15).',
            'Pause a few seconds, then pour to ' + water + ' g by about 1:45.',
            'Give the dripper one gentle swirl to flatten the bed and let it drain. You\'re aiming to finish around ' + time + '.'];
        }
        return {
          stats: [
            { label: 'Coffee', value: dose + ' g', sub: 'about ' + tbsp(dose) + ' tbsp ground' },
            { label: 'Water', value: water + ' g', sub: oz(water) + ' oz' },
            { label: 'Ratio', value: '1:' + ratio },
            tempStat(temp),
            grindStat(grindKey, o),
            { label: 'Total time', value: time }
          ],
          steps: steps,
          tips: [
            'Draining too fast and tasting sour? Grind a little finer. Slow and bitter? A little coarser.',
            'A gooseneck kettle makes slow, steady pours much easier, but any kettle works if you pour gently.',
            'No scale? ' + dose + ' g is about ' + tbsp(dose) + ' level tablespoons of ground coffee.'
          ],
          adjustNote: o.temp_f ? '' : ROAST_NOTE[b],
          numbers: { dose: dose, water: water, ratio: ratio, temp: temp, time: time }
        };
      }
    },

    drip: {
      size: { label: 'Mugs', min: 1, max: 5, def: 2, unit: function (n) { return n + (n === 1 ? ' mug' : ' mugs') + ' (' + n * 12 + ' oz)'; } },
      build: function (x) {
        var o = x.o, ratio = o.ratio || 16.5;
        var water = round(340 * (x.size || 2));
        var dose = round(water / ratio);
        var machineCups = Math.round(water / 148);
        return {
          stats: [
            { label: 'Coffee', value: dose + ' g', sub: 'about ' + tbsp(dose) + ' tbsp ground' },
            { label: 'Water', value: oz(water) + ' oz', sub: 'about ' + machineCups + ' on the carafe' },
            { label: 'Ratio', value: '1:' + ratio },
            grindStat('drip', o)
          ],
          steps: [
            'Fill the reservoir with ' + oz(water) + ' oz of fresh, cold water (filtered if you can). Most carafes count 5 oz as a "cup," so that\'s about ' + machineCups + ' on the markings.',
            'Set a paper filter in the basket. If you have a moment, rinse it with hot water first.',
            'Add ' + dose + ' g of coffee (about ' + tbsp(dose) + ' level tablespoons) and give the basket a gentle shake to level it.',
            'Brew. When it\'s done, give the pot a quick stir. The first and last drips taste different.',
            'Pour within 20 minutes, or move it to a thermos. A warming plate keeps cooking the coffee and turns it bitter.'
          ],
          tips: [
            'Buying ground? Our "Ground for Drip" option is ground for exactly this.',
            'Weak no matter what? Try more coffee before anything else. It\'s the most common fix.'
          ],
          adjustNote: '',
          numbers: { dose: dose, water: water, ratio: ratio }
        };
      }
    },

    french: {
      size: { label: 'Mugs', min: 1, max: 4, def: 2, unit: function (n) { return n + (n === 1 ? ' mug' : ' mugs') + ' (' + Math.round(n * 8.5) + ' oz)'; } },
      build: function (x) {
        var b = roastBucket(x.roast), o = x.o, ratio = o.ratio || 15;
        var water = r5(250 * (x.size || 2));
        var dose = round(water / ratio);
        var temp = o.temp_f || TEMP_F[b];
        return {
          stats: [
            { label: 'Coffee', value: dose + ' g', sub: 'about ' + tbsp(dose) + ' tbsp ground' },
            { label: 'Water', value: water + ' g', sub: oz(water) + ' oz' },
            { label: 'Ratio', value: '1:' + ratio },
            tempStat(temp),
            grindStat('french', o),
            { label: 'Total time', value: o.time || '4 min steep', sub: '+ 5 min to settle (optional)' }
          ],
          steps: [
            'Heat your water to ' + temp + '°F and grind ' + dose + ' g of coffee coarse, like sea salt.',
            'Add the coffee to the press, start a timer, and pour in ' + water + ' g of water so all the grounds get wet.',
            'Leave it alone for 4 minutes.',
            'At 4:00, stir the crust on top. Most of the grounds will sink. Spoon off any foam and floating bits.',
            'Worth the wait: let it sit another 4–5 minutes so the fine particles settle.',
            'Press the plunger just to the surface of the coffee, not all the way down. Pour gently and leave the last bit in the press.',
            'Pour it all out right away. Coffee left on the grounds keeps brewing and turns bitter.'
          ],
          tips: [
            'Pre-ground coffee for drip is a little fine for a press and can make the cup muddy. Whole bean ground coarse works best.',
            'No scale? ' + dose + ' g is about ' + tbsp(dose) + ' level tablespoons of ground coffee.'
          ],
          adjustNote: o.temp_f ? '' : ROAST_NOTE[b],
          numbers: { dose: dose, water: water, ratio: ratio, temp: temp }
        };
      }
    },

    aeropress: {
      build: function (x) {
        var b = roastBucket(x.roast), o = x.o;
        var dose = o.dose || 15, water = o.water || 230;
        var temp = o.temp_f || TEMP_F[b];
        return {
          stats: [
            { label: 'Coffee', value: dose + ' g', sub: 'about ' + tbsp(dose) + ' tbsp ground' },
            { label: 'Water', value: water + ' g', sub: oz(water) + ' oz' },
            { label: 'Ratio', value: '1:' + Math.round(water / dose * 10) / 10 },
            tempStat(temp),
            grindStat('aeropress', o),
            { label: 'Total time', value: o.time || 'About 2:00' }
          ],
          steps: [
            'Heat your water to ' + temp + '°F and grind ' + dose + ' g of coffee medium-fine.',
            'Put a paper filter in the cap, rinse it with hot water, and twist it onto the chamber. Set the AeroPress on a sturdy mug on your scale.',
            'Add the coffee, start a timer, and pour in ' + water + ' g of water.',
            'Stir 3 or 4 times, then set the plunger in about half an inch. That seal stops it from dripping through early.',
            'At 1:30, give it a gentle swirl, then press slowly for about 30 seconds. Stop when you hear a hiss.',
            'Taste. If it\'s strong, top it up with a little hot water. That\'s normal for AeroPress.'
          ],
          tips: [
            'Pressing feels like a workout? Your grind is too fine. Go a little coarser.',
            'Making iced coffee? Use 150 g of hot water and press directly over ice.'
          ],
          adjustNote: o.temp_f ? '' : ROAST_NOTE[b],
          numbers: { dose: dose, water: water, ratio: water / dose, temp: temp }
        };
      }
    },

    cold: {
      size: { label: 'Batch', min: 1, max: 3, def: 1, unit: function (n) { return ['Small batch', 'Medium batch', 'Large batch'][n - 1]; } },
      build: function (x) {
        var o = x.o, ratio = o.ratio || 5;
        var dose = 100 * (x.size || 1);
        var water = dose * ratio;
        var concentrate = r5(water - dose * 2);
        var drinks = Math.max(2, Math.floor(concentrate / 120));
        var cups = Math.round(dose / 90 * 4) / 4;
        return {
          stats: [
            { label: 'Coffee', value: dose + ' g', sub: 'about ' + cups + ' cups whole beans' },
            { label: 'Water', value: water + ' g', sub: oz(water) + ' oz, cold' },
            { label: 'Ratio', value: '1:' + ratio, sub: 'concentrate' },
            grindStat('cold', o),
            { label: 'Steep', value: o.time || '12–18 hours' },
            { label: 'Makes', value: 'About ' + drinks + ' drinks', sub: 'after mixing 1:1' }
          ],
          steps: [
            'Grind ' + dose + ' g of coffee extra coarse.',
            'Stir it into ' + water + ' g (' + oz(water) + ' oz) of cold, filtered water in a jar or pitcher until all the grounds are wet.',
            'Cover and steep 12–16 hours on the counter, or 16–20 hours in the fridge.',
            'Strain through a fine mesh strainer, then through a paper filter for a cleaner cup.',
            'This is a concentrate. Mix 1 part cold brew with 1 part water or milk over ice, then adjust to taste.',
            'Keep it covered in the fridge and enjoy it within about a week.'
          ],
          tips: [
            'Tastes flat or woody? Start with fresher beans and keep the steep under 20 hours.',
            'Want it ready to drink without mixing? Use 1:10 instead (' + dose + ' g coffee, ' + dose * 10 + ' g water).'
          ],
          adjustNote: '',
          numbers: { dose: dose, water: water, ratio: ratio }
        };
      }
    }
  };

  /* ---------- Troubleshooting: symptoms ---------- */
  var SYMPTOMS = [
    { id: 'sour', label: 'Sour or sharp', sub: 'Tart, lemony, a little thin', opposite: 'bitter' },
    { id: 'bitter', label: 'Bitter or harsh', sub: 'Dry, ashy, lingers in a bad way', opposite: 'sour' },
    { id: 'weak', label: 'Weak or watery', sub: 'Tastes thin, like it\'s missing something', opposite: 'strong' },
    { id: 'strong', label: 'Too strong', sub: 'Heavy, intense, hard to finish', opposite: 'weak' },
    { id: 'flat', label: 'Flat or dull', sub: 'Not much flavor at all' },
    { id: 'fast', opposite: 'slow', methods: {
      espresso: ['Shot runs too fast', 'Gushes out in under 20 seconds'],
      pourover: ['Drains too fast', 'Done well before the target time'],
      aeropress: ['Drips through before I press', 'Coffee leaks out while it steeps'] } },
    { id: 'slow', opposite: 'fast', methods: {
      espresso: ['Shot runs too slow', 'Barely drips, or takes 40+ seconds'],
      pourover: ['Drains too slowly', 'Water sits in the filter'],
      drip: ['Basket overflows', 'Or brewing takes forever'],
      french: ['Hard to plunge', 'The plunger fights back'],
      aeropress: ['Really hard to press', 'Takes real muscle'] } },
    { id: 'muddy', label: 'Gritty or muddy', sub: 'Sludge at the bottom of the cup', only: ['french', 'cold', 'aeropress'] },
    { id: 'inconsistent', label: 'Great one day, off the next', sub: 'Hard to repeat a good cup' },
    { id: 'other', label: 'Something else', sub: 'Or I\'m not sure how to describe it' }
  ];

  /* ---------- Troubleshooting: optional numbers per method ----------
   * Targets are what "about right" means. Anything outside sets a flag the rules use.
   */
  var DETAILS = {
    espresso: {
      fields: [
        { id: 'dose', label: 'Coffee in', unit: 'g', ph: '18' },
        { id: 'out', label: 'Espresso out', unit: 'g', ph: '36' },
        { id: 'time', label: 'Shot time', unit: 'sec', ph: '28', chips: true }
      ],
      ratio: [1.7, 2.7], time: [22, 38]
    },
    pourover: {
      fields: [
        { id: 'dose', label: 'Coffee', unit: 'g', ph: '20' },
        { id: 'water', label: 'Water', unit: 'g', ph: '320' },
        { id: 'time', label: 'Total brew time', unit: 'm:ss', ph: '3:15', chips: true, mmss: true },
        { id: 'temp', label: 'Water temperature', tempChips: true }
      ],
      ratio: [14.5, 17.5],
      timeBy: { v60: [150, 240], kalita: [165, 255], chemex: [210, 330], other: [150, 270] }
    },
    drip: {
      fields: [
        { id: 'dose', label: 'Coffee', unit: 'g', ph: '42', hint: 'No scale? 1 level tablespoon ≈ 5 g.' },
        { id: 'water', label: 'Water', unit: 'g or ml', ph: '680' }
      ],
      ratio: [14.5, 17.5]
    },
    french: {
      fields: [
        { id: 'dose', label: 'Coffee', unit: 'g', ph: '33' },
        { id: 'water', label: 'Water', unit: 'g', ph: '500' },
        { id: 'steep', label: 'Steep time', unit: 'min', ph: '4' },
        { id: 'temp', label: 'Water temperature', tempChips: true }
      ],
      ratio: [12.5, 16.5], steep: [3.5, 9]
    },
    aeropress: {
      fields: [
        { id: 'dose', label: 'Coffee', unit: 'g', ph: '15' },
        { id: 'water', label: 'Water', unit: 'g', ph: '230' },
        { id: 'steep', label: 'Steep time', unit: 'min', ph: '1.5' },
        { id: 'temp', label: 'Water temperature', tempChips: true }
      ],
      ratio: [11, 17], steep: [1, 3.5]
    },
    cold: {
      fields: [
        { id: 'dose', label: 'Coffee', unit: 'g', ph: '100' },
        { id: 'water', label: 'Water', unit: 'g', ph: '500' },
        { id: 'steep', label: 'Steep time', unit: 'hours', ph: '16' }
      ],
      ratio: [4, 12], steep: [12, 22]
    }
  };

  var TIME_CHIPS = [
    { id: 'fast', label: 'Fast' }, { id: 'ok', label: 'About right' }, { id: 'slow', label: 'Slow' }, { id: 'unknown', label: 'Not sure' }
  ];
  var TEMP_CHIPS = [
    { id: 'hot', label: 'Just off the boil' }, { id: 'ok', label: 'Rested a minute' }, { id: 'cool', label: 'Cooler than that' }, { id: 'unknown', label: 'Not sure' }
  ];

  /* ---------- Troubleshooting: fixes ----------
   * steps can be an array or a function(ctx) returning one. ctx has:
   *   machine, grinder, brewer, roast, recipe (starting recipe numbers), d (user numbers), flags
   * dir: '<group>:<direction>' lets the engine avoid contradicting itself and split the difference.
   * repeat: how many times "better, but not quite" can repeat this fix.
   */
  function grindFinerStep(c) {
    if (c.machine && c.machine.grinder === 'builtin') return c.machine.finer;
    if (c.grinder === 'blade') return 'Grind about 3–5 seconds longer, shaking the grinder as it runs.';
    return 'Move your grinder one small step finer (one click or one number).';
  }
  function grindCoarserStep(c) {
    if (c.machine && c.machine.grinder === 'builtin') return c.machine.coarser;
    if (c.grinder === 'blade') return 'Grind a few seconds less, in short pulses, shaking the grinder as it runs.';
    return 'Move your grinder one small step coarser (one click or one number).';
  }
  function doseOf(c) { return c.d.dose || c.recipe.dose; }
  function espOut(c) { return c.d.out || c.recipe.out; }
  function sameRecipe(c) {
    if (c.method !== 'espresso') return ['Brew the same recipe, then taste and compare.'];
    return ['Keep your dose at ' + doseOf(c) + ' g and stop the shot at ' + espOut(c) + ' g.', 'Time it. You\'re aiming for about 25–32 seconds.', 'Taste and compare.'];
  }

  var FIXES = {
    /* --- shared grind fixes --- */
    grindFiner: { title: 'Grind a little finer', dir: 'grind:finer', repeat: 2,
      why: 'Finer grounds give the water more to work with. That pulls out more sweetness and body and calms down sour, thin flavors.',
      steps: function (c) { return [grindFinerStep(c)].concat(sameRecipe(c)); } },
    grindCoarser: { title: 'Grind a little coarser', dir: 'grind:coarser', repeat: 2,
      why: 'Coarser grounds let water move through faster and pull out less, which softens bitter, drying flavors.',
      steps: function (c) { return [grindCoarserStep(c)].concat(sameRecipe(c)); } },

    /* --- espresso --- */
    espPreground: { title: 'Use the pressurized basket for pre-ground coffee',
      why: 'Pre-ground coffee is ground for drip, which is far too coarse for a standard espresso basket. Water rushes straight through, so the shot runs fast and tastes sour and thin.',
      steps: ['If your machine came with a pressurized (dual wall) basket, use it with pre-ground coffee.', 'For the best shots, grind whole beans fresh, right before brewing.'] },
    espBlade: { title: 'A burr grinder will make the biggest difference',
      why: 'Blade grinders chop beans into a mix of dust and chunks. Espresso is very sensitive to that, so shots run unevenly no matter what else you change.',
      steps: ['For now, use the pressurized (dual wall) basket if your machine came with one. It\'s much more forgiving.', 'When you\'re ready to upgrade, an espresso-capable burr grinder is the best investment you can make for home espresso.'] },
    espPressurizedRatio: { title: 'Let the shot run a little longer', dir: 'ratio:longer',
      why: 'Pressurized baskets do a lot of the work for you, so grind matters less. A slightly longer shot pulls out more sweetness.',
      steps: function (c) { return ['Keep your dose at ' + doseOf(c) + ' g.', 'Stop the shot at about ' + Math.round(doseOf(c) * 2.4) + ' g instead of ' + espOut(c) + ' g.', 'Make sure your beans are fresh. It matters even more with this basket.']; } },
    espLonger: { title: 'Let a little more espresso into the cup', dir: 'ratio:longer',
      why: 'A slightly longer shot dissolves more of the coffee\'s sweetness, which balances sharp, sour flavors.',
      steps: function (c) {
        var d = doseOf(c), now = c.d.out || Math.round(d * 2);
        return ['Keep your dose at ' + d + ' g.', 'Stop the shot at ' + Math.min(Math.round(now + d * 0.3), Math.round(d * 2.6)) + ' g instead of ' + now + ' g.', 'Don\'t worry if it runs a few seconds longer.'];
      } },
    espShorter: { title: 'Stop the shot a little sooner', dir: 'ratio:shorter',
      why: 'The end of a shot is mostly bitterness and water. Cutting it a bit earlier keeps the sweet, rich part.',
      steps: function (c) {
        var d = doseOf(c), now = c.d.out || Math.round(d * 2.2);
        return ['Keep your dose at ' + d + ' g.', 'Stop the shot at ' + Math.max(Math.round(now - d * 0.3), Math.round(d * 1.6)) + ' g instead of ' + now + ' g.'];
      } },
    espDose: { title: 'Match your dose to your basket',
      why: 'An overfilled basket chokes and pours unevenly. An underfilled one runs fast and watery. Getting the dose right makes every other change work better.',
      steps: function (c) {
        var target = c.machine ? c.machine.dose : 18;
        return ['Use ' + target + ' g of coffee (you\'re using ' + c.d.dose + ' g).', 'Aim for about ' + target * 2 + ' g of espresso out.', 'If your basket is stamped with a size, use that number instead.'];
      } },
    espPuck: { title: 'Even out your puck',
      why: 'If the grounds are clumpy or tamped crooked, water finds the easy path and skips the rest. That can taste sour and bitter at the same time.',
      steps: ['After grinding, stir out clumps with a thin needle or an unbent paperclip, or give the portafilter a few gentle taps on the counter.', 'Tamp level and firm, until the coffee stops compressing. No need to lean your whole weight on it.', 'Wipe loose grounds off the rim before locking in.'] },
    espWarmup: { title: 'Give the machine time to fully heat up',
      why: 'A cool group head or portafilter brews cooler than it should, which leads to sour, thin shots.',
      steps: function (c) { return [(c.machine && c.machine.warmup) || 'Give your machine at least 15 minutes to heat up with the portafilter locked in.', 'Right before brewing, run a short shot of plain water through the empty portafilter.']; } },
    espClean: { title: 'Clean the group head and basket',
      why: 'Old coffee oils build up quickly and add a stale, bitter edge to every shot.',
      steps: ['Rinse the basket and wipe the shower screen after each session.', 'If your manual allows it, backflush with a blind basket and espresso machine cleaner.', 'Descale on the schedule your manual recommends.'] },
    espDilute: { title: 'Make it a longer drink',
      why: 'Espresso is meant to be intense. If the flavor is good but it\'s a lot, add water or milk instead of changing the shot.',
      steps: ['For an Americano, add 4–6 oz of hot water.', 'For a latte, add 6–8 oz of steamed milk.'] },
    espWeigh: { title: 'Weigh every shot for a few days',
      why: 'Small changes in dose make a big difference in espresso. Weighing what goes in and what comes out is the fastest way to consistent shots.',
      steps: function (c) { return ['Weigh ' + doseOf(c) + ' g of ground coffee every time.', 'Put a scale under your cup and stop at ' + espOut(c) + ' g.', 'Note the time. When you adjust, change one thing at a time.']; } },

    /* --- shared water, ratio and technique fixes --- */
    hotter: { title: 'Use hotter water', dir: 'temp:hotter',
      why: 'Hotter water pulls out more sweetness, which balances sour, sharp notes. Lighter roasts especially like it hot.',
      steps: ['Heat your water to 205°F (96°C), or use it about 30 seconds off the boil.'] },
    cooler: { title: 'Let the water cool a little', dir: 'temp:cooler',
      why: 'Darker roasts give up their flavor easily. Slightly cooler water keeps them rich instead of harsh.',
      steps: ['Aim for 195°F (90°C), or let boiled water rest for about a minute.'] },
    moreCoffee: { title: 'Use a little more coffee', dir: 'ratio:stronger', repeat: 1,
      why: 'Weak and watery almost always means there isn\'t enough coffee for the water.',
      steps: function (c) {
        var w = c.d.water || c.recipe.water, r = c.method === 'cold' ? 5 : 16;
        if (c.method === 'french') r = 15;
        if (c.method === 'aeropress') r = 14;
        return ['Use ' + Math.round(w / r) + ' g of coffee for ' + w + ' g of water (1:' + r + ').', 'Keep the grind and everything else the same.'];
      } },
    moreWater: { title: 'Use a little more water', dir: 'ratio:weaker', repeat: 1,
      why: 'If the flavor is good but it\'s too intense, the easiest fix is a little more water for the same coffee.',
      steps: function (c) {
        var d = c.d.dose || c.recipe.dose, r = c.method === 'french' ? 15 : 16.5;
        if (c.method === 'aeropress') return ['After pressing, top up with hot water to taste. Start with 50 g.'];
        return ['Use ' + Math.round(d * r) + ' g of water for ' + d + ' g of coffee (1:' + r + ').', 'Or keep your recipe and add a splash of hot water to the cup.'];
      } },
    bloom: { title: 'Give it a proper bloom',
      why: 'Fresh coffee holds gas that pushes water away. A 45-second bloom lets that gas escape so the water can reach all the grounds.',
      steps: function (c) { var d = c.d.dose || c.recipe.dose; return ['Start with about ' + Math.round(d * 2.5) + ' g of water, just enough to wet the grounds.', 'Give it a gentle swirl so there are no dry pockets.', 'Wait 45 seconds before your next pour.']; } },
    swirl: { title: 'Add a gentle swirl',
      why: 'A light swirl after the bloom and after the last pour helps every ground brew evenly, which adds sweetness.',
      steps: ['Right after the bloom pour, swirl the dripper gently a few times.', 'After your final pour, give it one more gentle swirl to flatten the bed.'] },
    pourSlower: { title: 'Slow down your pours', dir: 'pace:slower',
      why: 'Slower pours give the water more time with the coffee, so it pulls out more flavor.',
      steps: function (c) { return ['Split your water into 3 or 4 smaller pours instead of 1 or 2 big ones.', 'Pour in slow circles, close to the coffee, and aim to finish around ' + (c.recipe.time || 'the target time') + '.']; } },
    pourGentler: { title: 'Pour more gently', dir: 'pace:gentler',
      why: 'Heavy, splashy pours stir up fine particles that clog the filter and turn bitter.',
      steps: ['Pour low and slow, in small circles.', 'Skip any stirring at the end. One gentle swirl is plenty.'] },
    chemexFilter: { title: 'Check your Chemex filter',
      why: 'If the filter seals against the spout, air can\'t escape and the brew stalls.',
      steps: ['Put the thick, triple-folded side of the filter over the spout.', 'Make sure the filter isn\'t pressed flat into the spout channel so air can escape as it brews.'] },
    blade: { title: 'Get more even grounds',
      why: 'Blade grinders make a mix of fine dust and big chunks. The dust over-brews and turns bitter while the chunks under-brew and taste sour.',
      steps: ['Grind in short pulses and shake the grinder as it runs.', 'If you can, a burr grinder is the biggest single upgrade for better coffee at home.'] },
    weigh: { title: 'Use a scale and a timer',
      why: 'Scoops and eyeballing vary more than you\'d think. Weighing coffee and water is the quickest way to the same great cup every day.',
      steps: function (c) { var d = c.recipe.dose, w = c.recipe.water; return ['Weigh ' + d + ' g of coffee and ' + w + ' g of water.', 'Use a timer and the same pour pattern each time.', 'When something tastes off, change one thing at a time.']; } },

    /* --- drip --- */
    dripMore: { title: 'Use more coffee', dir: 'ratio:stronger', repeat: 1,
      why: 'Weak drip coffee is almost always a ratio problem. Most people use a lot less coffee than their machine needs.',
      steps: function (c) {
        if (c.d.water) return ['Use ' + Math.round(c.d.water / 16.5) + ' g of coffee for your ' + Math.round(c.d.water / 29.57) + ' oz of water.', 'That\'s about ' + Math.round(c.d.water / 16.5 / 5) + ' level tablespoons.'];
        return ['Use about 2 level tablespoons (10 g) of coffee for every 6 oz of water.', 'For a full 10-cup pot (50 oz of water), that\'s about 90 g, or roughly 18 level tablespoons.'];
      } },
    dripLess: { title: 'Use a little less coffee', dir: 'ratio:weaker', repeat: 1,
      why: 'If the flavor is good but too intense, use less coffee for the same water.',
      steps: function (c) {
        if (c.d.water) return ['Use ' + Math.round(c.d.water / 17) + ' g of coffee for your ' + Math.round(c.d.water / 29.57) + ' oz of water.'];
        return ['Use 1 level tablespoon less coffee per 2 mugs of water.', 'Or add a splash of hot water to your cup.'];
      } },
    hotPlate: { title: 'Get it off the hot plate',
      why: 'Coffee keeps cooking on a warming plate. After about 20 minutes it turns bitter and flat.',
      steps: ['Move it to a thermos or insulated carafe right after brewing.', 'Or brew a smaller batch you\'ll finish within 20 minutes.'] },
    dripClean: { title: 'Descale and clean the machine',
      why: 'Mineral buildup lowers the brewing temperature, and old coffee oils taste stale and bitter.',
      steps: ['Run a descaling solution (or equal parts white vinegar and water) through the machine.', 'Follow with 2 or 3 full cycles of plain water.', 'Wash the basket and carafe with warm, soapy water.'] },
    dripPreheat: { title: 'Help your machine brew hotter',
      why: 'Many home machines brew cooler than ideal, which can make coffee taste sour and thin.',
      steps: ['Run one cycle of plain water to heat everything up, then brew.', 'Rinse the carafe with hot water before you start.'] },
    dripOverflow: { title: 'Stop the overflow',
      why: 'An overflowing basket usually means water can\'t get through the coffee and filter fast enough.',
      steps: function (c) {
        var s = ['Make sure you\'re using the right filter size and it sits fully in the basket.', 'Level the coffee, but don\'t pack it down.'];
        if (c.grinder === 'burr' || c.grinder === 'blade') s.push('Grind a little coarser.');
        s.push('If you\'re brewing a full pot, try about 10% less coffee.');
        return s;
      } },

    /* --- french press --- */
    steepLonger: { title: 'Steep a little longer', dir: 'steep:longer', repeat: 1,
      why: 'Coarse grounds need time. A longer steep pulls out more sweetness and body.',
      steps: function (c) {
        if (c.method === 'cold') return ['Add 2–4 hours to your steep time.', 'Keep everything else the same.'];
        if (c.method === 'aeropress') return ['Let it steep 2:00 before you press instead of 1:30.'];
        return ['Steep 5–6 minutes before you stir and skim.', 'Then let it settle and pour as usual.'];
      } },
    steepShorter: { title: 'Steep a little shorter', dir: 'steep:shorter', repeat: 1,
      why: 'A long steep keeps pulling flavor long after the good stuff is out, which turns bitter.',
      steps: function (c) {
        if (c.method === 'cold') return ['Cut your steep time by 2–4 hours.', 'Strain promptly and move it to the fridge.'];
        if (c.method === 'aeropress') return ['Press at 1:00 instead of 1:30.'];
        return ['Stir and skim at 3:30 instead of 4:00.'];
      } },
    pourOff: { title: 'Pour it all out after pressing',
      why: 'Coffee left sitting on the grounds keeps brewing and gets more bitter by the minute.',
      steps: ['As soon as you press, pour everything into mugs or a carafe.', 'Don\'t leave any in the press for a second cup later.'] },
    settle: { title: 'Let it settle before you pour',
      why: 'Most of the grit is tiny particles that float around right after brewing. Give them a few minutes and they sink.',
      steps: ['After stirring at 4:00, skim off the foam and floating bits.', 'Wait 4–5 more minutes.', 'Press just to the surface, not all the way down, and pour slowly. Leave the last bit in the press.'] },
    pressPreground: { title: 'Go coarser than pre-ground',
      why: 'Coffee ground for drip machines is finer than a French press wants. The fine particles slip through the mesh and make the cup muddy.',
      steps: ['Buy whole bean and grind coarse, like sea salt, or ask for a coarse grind.', 'Until then, let it settle 5 minutes before pressing and pour slowly.'] },
    gentlePlunge: { title: 'Press slowly and gently',
      why: 'Pushing hard forces fine grounds through the filter and stirs up bitterness.',
      steps: function (c) {
        if (c.method === 'aeropress') return ['Press with light, steady pressure for about 30 seconds.', 'Stop as soon as you hear a hiss.'];
        return ['Press slowly, using only the weight of your hand.', 'If it fights back, pull the plunger up a bit and try again gently.'];
      } },

    /* --- aeropress --- */
    apDilute: { title: 'Add water after pressing',
      why: 'AeroPress makes a strong, concentrated cup on purpose. Topping it up is part of the recipe.',
      steps: ['After pressing, add hot water to taste. Start with 50–100 g.'] },
    apSeal: { title: 'Seal it while it steeps',
      why: 'Without a seal, water drips through the filter before it\'s had time to brew.',
      steps: ['Right after stirring, set the plunger in about half an inch. The vacuum stops the drip.', 'Or try the inverted method: assemble it upside down, brew, then flip it onto your mug.'] },

    /* --- cold brew --- */
    cbFilter: { title: 'Strain it twice',
      why: 'Fine particles keep extracting in your fridge and make cold brew muddy and bitter.',
      steps: ['Strain through a fine mesh strainer first.', 'Then pour it through a paper coffee filter. It\'s slow, but worth it.'] },
    cbDiluteLess: { title: 'Mix it stronger', dir: 'ratio:stronger', repeat: 1,
      why: 'Your concentrate may be fine. You might just be adding too much water, milk or ice.',
      steps: ['Try 2 parts cold brew to 1 part water or milk.', 'Use larger ice cubes so it dilutes more slowly.'] },
    cbDiluteMore: { title: 'Mix it lighter', dir: 'ratio:weaker', repeat: 1,
      why: 'Cold brew concentrate is meant to be diluted. If it tastes heavy, add more water or milk.',
      steps: ['Try 1 part cold brew to 2 parts water or milk.'] },
    cbFresh: { title: 'Drink it fresher',
      why: 'Cold brew slowly loses its sweetness in the fridge. After about a week it tastes flat and woody.',
      steps: ['Make smaller batches you\'ll finish within a week.', 'Keep it sealed and cold.'] },

    /* --- universal --- */
    freshness: { title: 'Check your beans\' roast date',
      why: 'Coffee is at its best for a few weeks after roasting. Old or long-opened coffee loses flavor fast, and no recipe change can bring it back.',
      steps: ['Look for the roast date on the bag.', 'Store beans sealed in the bag or an airtight container, away from heat and light.', 'Grind right before you brew if you can.'] },
    water: { title: 'Try filtered water',
      why: 'Coffee is about 98% water. Very hard, softened or chlorinated tap water can make it taste flat, chalky or just off.',
      steps: ['Brew with water from a pitcher filter or your fridge filter.', 'Skip distilled water. Coffee needs a few minerals to taste right.'] },
    clean: { title: 'Give your gear a good clean',
      why: 'Coffee oils build up on everything they touch and add a stale, bitter taste over time.',
      steps: ['Wash your brewer, carafe and filter holder with warm, soapy water.', 'Brush out your grinder, or run a grinder-cleaning tablet through it.'] }
  };

  /* ---------- Troubleshooting: rules ----------
   * For each method and symptom, a list of [fixId, priority, conditions].
   * Conditions are flags that must all be true ('!flag' means must be false).
   * Flags: fast, slow, ratioLow, ratioHigh, doseHigh, doseLow, light, dark,
   *        preground, blade, burr, canGrind, builtin, pressurized, chemex,
   *        tempHot, tempCool, steepShort, steepLong, numbersKnown
   * The engine picks the highest-priority fix that applies and hasn't been tried.
   */
  var RULES = {
    espresso: {
      sour: [['espPreground', 100, ['preground', '!pressurized']], ['espBlade', 95, ['blade']], ['espPressurizedRatio', 90, ['pressurized']],
        ['espDose', 88, ['doseHigh']], ['espLonger', 86, ['ratioLow']], ['grindFiner', 84, ['fast', 'canGrind']],
        ['espDose', 70, ['doseLow']], ['grindFiner', 65, ['!slow', 'canGrind', '!pressurized']], ['espLonger', 60, ['slow']],
        ['espWarmup', 50], ['espLonger', 45, ['light']], ['espPuck', 40]],
      bitter: [['espBlade', 95, ['blade']], ['espShorter', 86, ['ratioHigh']], ['grindCoarser', 84, ['slow', 'canGrind']],
        ['espDose', 75, ['doseHigh']], ['espClean', 70, ['pressurized']], ['grindCoarser', 62, ['!fast', 'canGrind', '!pressurized']],
        ['espShorter', 58], ['espPuck', 50, ['fast']], ['espClean', 45]],
      weak: [['espPreground', 100, ['preground', '!pressurized']], ['espBlade', 95, ['blade']], ['espShorter', 88, ['ratioHigh']],
        ['espDose', 85, ['doseLow']], ['grindFiner', 80, ['fast', 'canGrind']], ['espShorter', 60], ['grindFiner', 55, ['!slow', 'canGrind', '!pressurized']], ['espPuck', 45]],
      strong: [['espLonger', 85, ['ratioLow']], ['espDose', 80, ['doseHigh']], ['espDilute', 70], ['espLonger', 60]],
      flat: [['espPreground', 90, ['preground', '!pressurized']], ['freshness', 85], ['espLonger', 55, ['light']], ['grindFiner', 50, ['!slow', 'canGrind', '!pressurized']], ['espClean', 45]],
      fast: [['espPreground', 100, ['preground', '!pressurized']], ['espBlade', 95, ['blade']], ['espDose', 85, ['doseLow']],
        ['grindFiner', 82, ['canGrind']], ['espPuck', 60], ['freshness', 40]],
      slow: [['grindCoarser', 90, ['canGrind']], ['espDose', 85, ['doseHigh']], ['espPuck', 50], ['espClean', 40]],
      inconsistent: [['espWeigh', 90, ['!numbersKnown']], ['espBlade', 88, ['blade']], ['espPuck', 80], ['espWarmup', 70], ['espWeigh', 50], ['freshness', 40]],
      other: []
    },
    pourover: {
      sour: [['blade', 90, ['blade']], ['grindFiner', 88, ['fast', 'canGrind']], ['pourSlower', 84, ['fast']], ['hotter', 80, ['tempCool']],
        ['hotter', 72, ['light', '!tempHot']], ['grindFiner', 66, ['!slow', 'canGrind']], ['bloom', 55], ['swirl', 50], ['hotter', 45, ['!tempHot']]],
      bitter: [['blade', 90, ['blade']], ['chemexFilter', 86, ['chemex', 'slow']], ['grindCoarser', 85, ['slow', 'canGrind']], ['cooler', 80, ['tempHot', 'dark']],
        ['pourGentler', 70, ['slow']], ['grindCoarser', 62, ['!fast', 'canGrind']], ['moreWater', 58, ['ratioLow']], ['cooler', 55, ['dark', '!tempCool']],
        ['cooler', 52, ['tempHot']], ['pourGentler', 50]],
      weak: [['moreCoffee', 92, ['ratioHigh']], ['grindFiner', 80, ['fast', 'canGrind']], ['pourSlower', 75, ['fast']], ['moreCoffee', 65, ['!ratioLow']], ['grindFiner', 55, ['!slow', 'canGrind']]],
      strong: [['moreWater', 90, ['ratioLow']], ['moreWater', 70], ['grindCoarser', 50, ['slow', 'canGrind']]],
      flat: [['freshness', 85], ['water', 70], ['hotter', 60, ['!tempHot']], ['bloom', 50]],
      fast: [['grindFiner', 90, ['canGrind']], ['pourSlower', 85], ['moreCoffee', 60, ['ratioHigh']]],
      slow: [['chemexFilter', 92, ['chemex']], ['blade', 88, ['blade']], ['grindCoarser', 86, ['canGrind']], ['pourGentler', 75]],
      inconsistent: [['weigh', 90, ['!numbersKnown']], ['blade', 85, ['blade']], ['bloom', 60], ['pourSlower', 50], ['freshness', 40]],
      other: []
    },
    drip: {
      sour: [['dripMore', 85, ['ratioHigh']], ['grindFiner', 70, ['burr']], ['dripPreheat', 65], ['dripMore', 55, ['!ratioLow']], ['dripClean', 50]],
      bitter: [['hotPlate', 90], ['dripClean', 80], ['grindCoarser', 70, ['burr']], ['dripLess', 60, ['ratioLow']], ['blade', 55, ['blade']]],
      weak: [['dripMore', 95, ['!ratioLow']], ['grindFiner', 70, ['burr']], ['dripPreheat', 60]],
      strong: [['dripLess', 90], ['grindCoarser', 50, ['burr']]],
      flat: [['hotPlate', 88], ['freshness', 85], ['dripClean', 75], ['water', 70]],
      slow: [['dripOverflow', 95], ['dripClean', 70]],
      inconsistent: [['weigh', 90, ['!numbersKnown']], ['hotPlate', 70], ['dripClean', 60], ['freshness', 40]],
      other: []
    },
    french: {
      sour: [['steepLonger', 88, ['steepShort']], ['hotter', 80, ['tempCool']], ['steepLonger', 70, ['!steepLong']], ['hotter', 60, ['!tempHot']], ['grindFiner', 50, ['burr']]],
      bitter: [['pourOff', 92], ['steepShorter', 86, ['steepLong']], ['grindCoarser', 75, ['canGrind']], ['cooler', 65, ['dark', '!tempCool']], ['pressPreground', 60, ['preground']], ['gentlePlunge', 50]],
      weak: [['moreCoffee', 92, ['ratioHigh']], ['steepLonger', 80, ['!steepLong']], ['moreCoffee', 65, ['!ratioLow']], ['grindFiner', 50, ['burr']]],
      strong: [['moreWater', 90]],
      flat: [['freshness', 85], ['steepLonger', 70, ['!steepLong']], ['water', 65]],
      muddy: [['pressPreground', 95, ['preground']], ['settle', 90], ['grindCoarser', 80, ['canGrind']], ['blade', 75, ['blade']], ['gentlePlunge', 60]],
      slow: [['pressPreground', 95, ['preground']], ['grindCoarser', 90, ['canGrind']], ['gentlePlunge', 80]],
      inconsistent: [['weigh', 90, ['!numbersKnown']], ['blade', 80, ['blade']], ['pourOff', 70], ['freshness', 40]],
      other: []
    },
    aeropress: {
      sour: [['steepLonger', 86, ['steepShort']], ['hotter', 82, ['tempCool']], ['grindFiner', 75, ['canGrind']], ['steepLonger', 65, ['!steepLong']], ['hotter', 55, ['!tempHot']]],
      bitter: [['gentlePlunge', 80], ['steepShorter', 78, ['steepLong']], ['grindCoarser', 75, ['canGrind']], ['cooler', 65, ['dark', '!tempCool']], ['steepShorter', 50, ['!steepShort']]],
      weak: [['moreCoffee', 90, ['ratioHigh']], ['grindFiner', 75, ['canGrind']], ['steepLonger', 65, ['!steepLong']], ['moreCoffee', 55, ['!ratioLow']]],
      strong: [['apDilute', 92]],
      flat: [['freshness', 85], ['hotter', 60, ['!tempHot']], ['water', 55]],
      fast: [['apSeal', 95], ['grindFiner', 70, ['canGrind']]],
      slow: [['grindCoarser', 90, ['canGrind']], ['gentlePlunge', 80]],
      muddy: [['grindCoarser', 80, ['canGrind']], ['gentlePlunge', 70]],
      inconsistent: [['weigh', 90, ['!numbersKnown']], ['blade', 80, ['blade']], ['freshness', 40]],
      other: []
    },
    cold: {
      sour: [['steepLonger', 88, ['!steepLong']], ['grindFiner', 50, ['burr']]],
      bitter: [['steepShorter', 88, ['steepLong']], ['cbFilter', 80], ['grindCoarser', 75, ['canGrind']], ['steepShorter', 60, ['!steepShort']]],
      weak: [['moreCoffee', 90, ['ratioHigh']], ['cbDiluteLess', 85], ['steepLonger', 70, ['!steepLong']]],
      strong: [['cbDiluteMore', 92]],
      flat: [['cbFresh', 88], ['freshness', 80], ['water', 60]],
      muddy: [['cbFilter', 95], ['grindCoarser', 80, ['canGrind']]],
      inconsistent: [['weigh', 90, ['!numbersKnown']], ['freshness', 50]],
      other: []
    }
  };

  // Always available at the end of any list, in this order, before we suggest reaching out.
  var FALLBACKS = [['freshness', 20], ['water', 15], ['clean', 10]];
  var MAX_ATTEMPTS = 5;

  return {
    roastBucket: roastBucket, mmss: mmss,
    METHODS: METHODS, MACHINES: MACHINES, BREWERS: BREWERS, GRINDERS: GRINDERS, BASKETS: BASKETS, ROASTS: ROASTS,
    RECIPES: RECIPES, SYMPTOMS: SYMPTOMS, DETAILS: DETAILS, TIME_CHIPS: TIME_CHIPS, TEMP_CHIPS: TEMP_CHIPS,
    FIXES: FIXES, RULES: RULES, FALLBACKS: FALLBACKS, MAX_ATTEMPTS: MAX_ATTEMPTS
  };
})();
